import React, { useState, useEffect, useReducer, useContext, useRef } from "react";
import { Link as RouterLink, useHistory } from "react-router-dom";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Chip from "@material-ui/core/Chip";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import Link from "@material-ui/core/Link";
import Grid from "@material-ui/core/Grid";
import MenuItem from "@material-ui/core/MenuItem";
import SearchIcon from "@material-ui/icons/Search";
import RefreshIcon from "@material-ui/icons/Refresh";
import AddIcon from "@material-ui/icons/Add";
import VisibilityIcon from "@material-ui/icons/Visibility";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";

import { parseISO, format } from "date-fns";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import MainContainer from "../../components/MainContainer";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import { AuthContext } from "../../context/Auth/AuthContext";
import useUsers from "../../hooks/useUsers";
import {
	statusLabel,
	statusColor,
} from "../../components/SolicitacoesDrawer/statusMeta";
import SolicitacaoDetailDialog from "../../components/SolicitacaoDetailDialog";
import SlaProgress from "../../components/SlaProgress";
import VisaoGeralPanel from "./VisaoGeralPanel";
import CatalogoTiposPanel from "./CatalogoTiposPanel";
import RelatoriosPanel from "./RelatoriosPanel";

const PAGE_SIZE = 20;

// Mesmo padrao de reducer ja usado em paginas de lista existentes (Contacts,
// Tickets) - acumula paginas carregadas, atualiza em vez de duplicar quando
// o mesmo id reaparece (ex.: apos um refresh manual).
const reducer = (state, action) => {
	if (action.type === "LOAD") {
		const items = action.payload;
		const newItems = [];
		items.forEach(item => {
			const index = state.findIndex(s => s.id === item.id);
			if (index !== -1) {
				state[index] = item;
			} else {
				newItems.push(item);
			}
		});
		return [...state, ...newItems];
	}
	if (action.type === "RESET") {
		return [];
	}
	return state;
};

// Backoffice (Etapa E) - exatamente as 5 categorias aprovadas, sem "Todas"
// nem "Canceladas" como aba principal. "Em Andamento" engloba os status
// tecnicos "created" e "in_progress" (o backend aceita status=X,Y desde
// esta etapa - ver ListOperationalRequestsService.ts) porque a UI
// apresenta "created" como "Em Andamento" (decisao aprovada: nenhum dado
// e migrado, so a apresentacao muda - ver statusMeta.js).
const STATUS_TABS = [
	{ key: "in_progress", statusParam: "created,in_progress" },
	{ key: "waiting_btg", statusParam: "waiting_btg" },
	{ key: "waiting_assessor_client", statusParam: "waiting_assessor_client" },
	{ key: "btg_refused", statusParam: "btg_refused" },
	{ key: "completed", statusParam: "completed" },
];
const STATUS_TAB_BY_KEY = STATUS_TABS.reduce((acc, tab) => {
	acc[tab.key] = tab;
	return acc;
}, {});

// "Canceladas" continua acessivel, mas deliberadamente FORA das 5 abas
// principais (decisao aprovada) - via um chip discreto ao lado da busca,
// nao uma 6a aba. Os registros cancelados continuam existindo/consultaveis
// normalmente, so nao ocupam uma categoria de destaque na tela.
const CANCELLED_KEY = "cancelled";
const CANCELLED_STATUS_PARAM = "cancelled";

// Fallback deliberado para o numero do ticket, e nao para "N/A"/"-": um
// traco joga fora informacao que existe, enquanto "#4085" ainda identifica o
// registro e continua clicavel. Nos dados de producao nenhum registro precisa
// disso (0 de 53 sem contact_name), mas o codigo nao deve assumir.
const nomeDoCliente = item =>
	item.contact_name || `#${item.ticket_whaticket_id}`;

// A busca aceita duas formas: so digitos procura pelo NUMERO da solicitacao
// (comportamento antigo, preservado); qualquer outro texto procura pelo NOME
// do cliente/assessor, via iLike na API.
//
// Devolve os dois parametros de uma vez porque a listagem e a contagem das
// abas precisam filtrar EXATAMENTE igual - se uma delas esquecer um dos
// parametros, o numero na aba deixa de bater com o que a lista mostra.
const filtrosDeBusca = termo => {
	const limpo = (termo || "").trim();
	if (!limpo) return { number: undefined, search: undefined };
	return /^\d+$/.test(limpo)
		? { number: limpo, search: undefined }
		: { number: undefined, search: limpo };
};

// Painel de filtros avancados (Tipo/Atendente/SLA) - complementa a busca
// (numero/cliente) e as abas (status), que ja existiam. Mesmas 4 situacoes
// de SLA_SITUACAO_SQL (ListOperationalRequestsService.ts na API), que por
// sua vez espelham calculateSla.ts.
//
// Os filtros de periodo (Criado em / Ultima atualizacao) que existiam aqui
// foram removidos a pedido (decisao aprovada) - "Criado em" continua
// existindo no backend (usado por RelatoriosPanel/VisaoGeralPanel), so nao e
// mais exposto nesta tela; "Ultima atualizacao" (updated_from/updated_to) foi
// removido de toda a cadeia (API, proxy, controller), por nao ter mais
// nenhum consumidor.
const SLA_SITUACOES = ["normal", "atencao", "critico", "vencido"];

const EMPTY_ADVANCED_FILTERS = {
	typeId: "",
	attendantId: "",
	slaSituacao: "",
};

const useStyles = makeStyles(theme => ({
	mainPaper: {
		flex: 1,
		overflowY: "auto",
		overflowX: "auto",
		...(theme.scrollbarStyles || {}),
	},
	statusChip: {
		fontWeight: 600,
	},
	emptyState: {
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		padding: theme.spacing(6, 2),
		color: theme.palette.text.secondary,
	},
	errorState: {
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		padding: theme.spacing(4, 2),
		color: theme.palette.error.main,
	},
	hideOnMobile: {
		[theme.breakpoints.down("sm")]: {
			display: "none",
		},
	},
	// Nome do cliente/assessor: chega a 36 caracteres nos dados reais, contra
	// 5 do antigo "#4085". Sem teto, a coluna alargaria a tabela inteira - que
	// ja tem 9 colunas e e um dos pontos criticos no mobile. Trunca com
	// reticencias; o nome completo fica no title (hover) da celula.
	clienteCell: {
		maxWidth: 220,
		overflow: "hidden",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
		[theme.breakpoints.down("xs")]: {
			maxWidth: 130,
		},
	},
	tabsRow: {
		display: "flex",
		alignItems: "center",
		borderBottom: `1px solid ${theme.palette.divider}`,
		paddingRight: theme.spacing(2),
	},
	tabsGrow: {
		flex: 1,
		minWidth: 0,
	},
	cancelledChip: {
		flexShrink: 0,
		marginLeft: theme.spacing(1),
	},
	tabLabel: {
		whiteSpace: "nowrap",
	},
	// O MUI aplica minWidth:160px por aba a partir do breakpoint "sm" - isso
	// estica abas com rotulo curto ("Recusa BTG (2)") alem do necessario,
	// criando um espaco vazio maior ao redor delas do que ao redor de abas
	// com rotulo longo (que ja preenchem os 160px sozinhas). Removendo o
	// minWidth e fixando um padding igual para todas, o espaco entre
	// qualquer par de abas fica identico, proporcional so ao texto.
	tabRoot: {
		minWidth: "auto",
		padding: theme.spacing(0, 2.5),
	},
	mainTabRoot: {
		minWidth: "auto",
		padding: theme.spacing(0, 2.5),
		textTransform: "none",
		fontWeight: 600,
		whiteSpace: "nowrap",
	},
	mainTabsRow: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		borderBottom: `1px solid ${theme.palette.divider}`,
		marginBottom: theme.spacing(1),
		// Mesmo padding-right do MainHeader do proprio Whaticket
		// (components/MainHeader/index.js: "0px 6px 6px 6px") - reproduz aqui
		// o mesmo respiro da borda direita que a barra de cima ja tem.
		paddingRight: 6,
	},
	// Mesma regra que MainHeaderButtonsWrapper aplica a cada botao seu
	// (components/MainHeaderButtonsWrapper/index.js: "& > *": { margin:
	// theme.spacing(1) }) - com isso "+ Novo Tipo" fica exatamente no mesmo
	// respiro/tamanho/posicao relativa que "+ Nova Solicitacao" tem la em
	// cima quando a aba Visao Geral esta ativa.
	novoTipoButton: {
		margin: theme.spacing(1),
	},
	// Mesmos valores de RelatoriosPanel/index.js (classes.paper/actions) e de
	// VisaoGeralPanel/index.js (classes.filtersPaper) - o pedido foi este
	// painel ficar visualmente igual aos das outras abas, entao usa o mesmo
	// respiro (padding do Paper e espaco entre os campos), nao um valor novo.
	filtersPaper: {
		padding: theme.spacing(2),
		marginBottom: theme.spacing(1),
		borderRadius: 12,
	},
	filtersActions: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(1.5),
		marginTop: theme.spacing(1),
	},
}));

// Escopo da listagem. A regra de acesso e do BACKEND, nao daqui: a API ja
// diferencia admin de user (ver ListOperationalRequestsService.ts) - admin
// sem attendant_id ve a empresa toda, admin com attendant_id ve so daquele
// atendente, e qualquer outro profile e forcado ao proprio id mesmo que
// tente mandar outro. Este seletor so escolhe SE o attendantId vai na query;
// nao afrouxa nada, e para profile=user ele nem aparece (o resultado seria
// identico de qualquer forma, porque a API ignora o que o navegador pediu).
//
// Padrao para admin e "todas": quem tem perfil gerencial abre a tela para
// acompanhar a fila da equipe, e um Head de Backoffice que nao e atendente
// de nada via a lista zerada em "minhas" - foi exatamente o relato que
// originou esta mudanca.
const SCOPE_MINHAS = "minhas";
const SCOPE_TODAS = "todas";
// Backoffice - Visao Geral: nivel de navegacao acima das 5 abas de status,
// nao um 6o status. "Solicitacoes" (as 5 abas + tabela, inalterado) e
// "Visao Geral" (novo, so admin) sao MODOS diferentes da mesma tela - por
// isso um estado separado (mainView), nunca misturado com statusFilter.
const MAIN_VIEW_SOLICITACOES = "solicitacoes";
const MAIN_VIEW_VISAO_GERAL = "visao_geral";
const MAIN_VIEW_CATALOGO = "catalogo_tipos";
const MAIN_VIEW_RELATORIOS = "relatorios";

const MinhasSolicitacoes = () => {
	const classes = useStyles();
	const history = useHistory();
	const { user } = useContext(AuthContext);
	const isAdmin = user.profile === "admin";

	const [mainView, setMainView] = useState(MAIN_VIEW_SOLICITACOES);
	// So a listagem de Solicitacoes usa escopo/busca/"Nova Solicitacao" - no
	// modo Catalogo esses controles nao tem efeito nenhum (nao filtram tipos),
	// entao ficam ocultos para nao confundir o que cada botao faz.
	const isCatalogoView = mainView === MAIN_VIEW_CATALOGO;
	// Relatorios tem filtros proprios dentro do painel - os controles do
	// cabecalho (escopo, busca por numero, atualizar, nova solicitacao) nao
	// tem efeito ali, entao ficam ocultos como no Catalogo.
	const hideListControls = isCatalogoView || mainView === MAIN_VIEW_RELATORIOS;
	// Admin abre em "todas"; user fica preso em "minhas" (e nao ve o seletor).
	const defaultScope = isAdmin ? SCOPE_TODAS : SCOPE_MINHAS;
	const [scope, setScope] = useState(defaultScope);
	const [loading, setLoading] = useState(false);
	const [page, setPage] = useState(1);
	const [hasMore, setHasMore] = useState(false);
	const [count, setCount] = useState(0);
	const [statusFilter, setStatusFilter] = useState(STATUS_TABS[0].key);
	const [searchParam, setSearchParam] = useState("");
	const [solicitacoes, dispatch] = useReducer(reducer, []);
	const [selectedId, setSelectedId] = useState(null);
	const [fetchError, setFetchError] = useState(null);
	const [reloadFlag, setReloadFlag] = useState(0);
	const [tabCounts, setTabCounts] = useState({});
	const [cancelledCount, setCancelledCount] = useState(0);
	// Painel de filtros (Exibir/Buscar/Tipo/Atendente/SLA) - Tipo/Atendente/SLA
	// ficam num estado proprio, separado de statusFilter/searchParam/scope
	// (que ja existiam antes deste painel), igual ao mesmo principio que ja
	// levou mainView a ficar fora de statusFilter. Painel sempre visivel
	// (nao e mais um Collapse) - reune TODOS os filtros da tela num unico
	// lugar organizado, em vez de parte no cabecalho e parte num painel
	// escondido atras de um botao (decisao aprovada).
	const [filters, setFilters] = useState(EMPTY_ADVANCED_FILTERS);
	const [requestTypes, setRequestTypes] = useState([]);
	const { users } = useUsers();
	// O botao "+ Novo Tipo" mora aqui (mesma linha das abas), mas quem sabe
	// abrir o modal de criacao e o CatalogoTiposPanel (tambem usado no clique
	// de Editar por linha) - ref evita duplicar o estado do modal no pai.
	const catalogoRef = useRef(null);

	const activeStatusParam =
		statusFilter === CANCELLED_KEY
			? CANCELLED_STATUS_PARAM
			: STATUS_TAB_BY_KEY[statusFilter].statusParam;

	// undefined em "todas" -> o parametro nao vai na query e a API devolve a
	// empresa inteira. Uma unica origem para lista, contagens das abas e chip
	// de canceladas, para os numeros nunca discordarem entre si. Em "todas",
	// o filtro "Atendente" do painel avancado pode restringir a UM atendente
	// especifico (mesmo parametro attendant_id, so que escolhido no painel em
	// vez de implicito pelo escopo); em "minhas" o atendente sempre e o
	// proprio usuario, e o campo do painel fica desabilitado (nao teria
	// efeito - o backend tambem ignoraria, ver ListOperationalRequestsService.ts).
	const scopedAttendantId =
		scope === SCOPE_TODAS ? filters.attendantId || undefined : user.id;

	// Usado so para habilitar/desabilitar o botao "Limpar filtros" - ele reseta
	// o painel inteiro (Tipo/Atendente/SLA/Buscar/Exibir), entao considera os
	// 5 juntos, nao so os 3 que ficam no estado `filters`.
	const hasActiveFilters =
		Object.values(filters).some(Boolean) ||
		!!searchParam.trim() ||
		scope !== defaultScope;

	useEffect(() => {
		const fetchTypes = async () => {
			try {
				const { data } = await api.get("/solicitacoes/tipos");
				setRequestTypes(data);
			} catch (err) {
				toastError(err);
			}
		};
		fetchTypes();
	}, []);

	useEffect(() => {
		dispatch({ type: "RESET" });
		setPage(1);
	}, [statusFilter, searchParam, reloadFlag, scope, filters]);

	useEffect(() => {
		setLoading(true);
		const delayDebounceFn = setTimeout(() => {
			const fetchSolicitacoes = async () => {
				try {
					const busca = filtrosDeBusca(searchParam);

					const { data } = await api.get("/solicitacoes", {
						params: {
							attendantId: scopedAttendantId,
							status: activeStatusParam,
							...busca,
							typeId: filters.typeId || undefined,
							slaSituacao: filters.slaSituacao || undefined,
							page,
							pageSize: PAGE_SIZE,
						},
					});
					dispatch({ type: "LOAD", payload: data.solicitacoes });
					setHasMore(data.hasMore);
					setCount(data.count);
					setFetchError(null);
				} catch (err) {
					setFetchError(err);
					toastError(err);
				} finally {
					setLoading(false);
				}
			};
			fetchSolicitacoes();
		}, 500);
		return () => clearTimeout(delayDebounceFn);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [statusFilter, searchParam, page, reloadFlag, scope, filters]);

	// Contagem numerica de cada aba ("Em Andamento (8)" etc.) + do chip
	// discreto de canceladas - UMA chamada a /solicitacoes/contagens (GROUP
	// BY status no backend), em vez de 6 GETs em /solicitacoes em paralelo
	// (um por status) so para pegar um numero de cada. Debounced junto com a
	// busca para nao disparar a cada tecla.
	useEffect(() => {
		const delayDebounceFn = setTimeout(() => {
			const fetchCounts = async () => {
				try {
					const busca = filtrosDeBusca(searchParam);

					const { data } = await api.get("/solicitacoes/contagens", {
						params: {
							attendantId: scopedAttendantId,
							...busca,
							typeId: filters.typeId || undefined,
							slaSituacao: filters.slaSituacao || undefined,
						},
					});

					const counts = {};
					STATUS_TABS.forEach(tab => {
						counts[tab.key] = data[tab.key] ?? 0;
					});
					setTabCounts(counts);
					setCancelledCount(data.cancelled ?? 0);
				} catch (err) {
					// Contagem e so um indicador auxiliar nas abas - uma falha aqui nao
					// deve travar a lista principal (ja tratada no efeito acima).
				}
			};
			fetchCounts();
		}, 500);
		return () => clearTimeout(delayDebounceFn);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchParam, reloadFlag, user.id, scope, filters]);

	const handleSearch = e => setSearchParam(e.target.value);

	const handleChangeTab = (e, newValue) => setStatusFilter(newValue);

	const handleFilterChange = field => e =>
		setFilters(prev => ({ ...prev, [field]: e.target.value }));

	// Reseta o painel inteiro - Tipo/Atendente/SLA, Buscar e Exibir -, ja que
	// agora os 5 vivem juntos no mesmo lugar (decisao aprovada).
	const handleClearFilters = () => {
		setFilters(EMPTY_ADVANCED_FILTERS);
		setSearchParam("");
		setScope(defaultScope);
	};

	const handleChangeMainView = (e, newValue) => setMainView(newValue);

	const handleNovoTipo = () => catalogoRef.current?.openCreate();

	const handleShowCancelled = () => setStatusFilter(CANCELLED_KEY);

	const handleRefresh = () => setReloadFlag(flag => flag + 1);

	const handleNovaSolicitacao = () => {
		// Nao existe contexto de ticket nesta pagina (a criacao sempre exigiu um
		// Ticket real, resolvido no backend - Fase 1). Em vez de duplicar/forcar
		// um fluxo de criacao paralelo sem ticket, direciona para a tela de
		// atendimentos, onde o botao "Nova Solicitacao" ja existente e testado
		// (Fase 1) funciona normalmente dentro de um ticket aberto.
		toast.info(i18n.t("minhasSolicitacoes.novaSolicitacaoRedirect"));
		history.push("/tickets");
	};

	const handleRowClick = id => setSelectedId(id);

	const handleStatusUpdated = () => {
		handleRefresh();
	};

	const loadMore = () => setPage(prev => prev + 1);

	const handleScroll = e => {
		if (!hasMore || loading) return;
		const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
		if (scrollHeight - (scrollTop + 100) < clientHeight) {
			loadMore();
		}
	};

	return (
		<MainContainer>
			<SolicitacaoDetailDialog
				open={!!selectedId}
				solicitacaoId={selectedId}
				onClose={() => setSelectedId(null)}
				onStatusUpdated={handleStatusUpdated}
			/>

			<MainHeader>
				<Title>
					{mainView === MAIN_VIEW_RELATORIOS ? (
						i18n.t("minhasSolicitacoes.mainTabs.relatorios")
					) : (
						<>
							{scope === SCOPE_TODAS
								? i18n.t("minhasSolicitacoes.titleTodas")
								: i18n.t("minhasSolicitacoes.title")}{" "}
							({count})
						</>
					)}
				</Title>
				<MainHeaderButtonsWrapper>
					{!hideListControls && (
						<Tooltip title={i18n.t("minhasSolicitacoes.refresh")}>
							<IconButton
								id="minhas-solicitacoes-refresh-btn"
								onClick={handleRefresh}
								disabled={loading}
							>
								<RefreshIcon />
							</IconButton>
						</Tooltip>
					)}
					{!hideListControls && (
						<Button
							id="minhas-solicitacoes-nova-btn"
							variant="contained"
							color="primary"
							startIcon={<AddIcon />}
							onClick={handleNovaSolicitacao}
						>
							{i18n.t("minhasSolicitacoes.newButton")}
						</Button>
					)}
				</MainHeaderButtonsWrapper>
			</MainHeader>

			{isAdmin && (
				<div className={classes.mainTabsRow}>
					<Tabs
						value={mainView}
						onChange={handleChangeMainView}
						indicatorColor="primary"
						textColor="primary"
					>
						<Tab
							id="minhas-solicitacoes-maintab-solicitacoes"
							value={MAIN_VIEW_SOLICITACOES}
							classes={{ root: classes.mainTabRoot }}
							label={i18n.t("minhasSolicitacoes.mainTabs.solicitacoes")}
						/>
						<Tab
							id="minhas-solicitacoes-maintab-visao-geral"
							value={MAIN_VIEW_VISAO_GERAL}
							classes={{ root: classes.mainTabRoot }}
							label={i18n.t("minhasSolicitacoes.mainTabs.visaoGeral")}
						/>
						<Tab
							id="minhas-solicitacoes-maintab-relatorios"
							value={MAIN_VIEW_RELATORIOS}
							classes={{ root: classes.mainTabRoot }}
							label={i18n.t("minhasSolicitacoes.mainTabs.relatorios")}
						/>
						<Tab
							id="minhas-solicitacoes-maintab-catalogo"
							value={MAIN_VIEW_CATALOGO}
							classes={{ root: classes.mainTabRoot }}
							label={i18n.t("minhasSolicitacoes.mainTabs.catalogo")}
						/>
					</Tabs>
					{/* Mesma linha das abas, extrema direita - so faz sentido com a
					    aba Demandas selecionada, entao nao cria espaco/margem
					    quando as outras abas estao ativas. */}
					{isCatalogoView && (
						<Button
							id="catalogo-tipos-novo-btn"
							className={classes.novoTipoButton}
							variant="contained"
							color="primary"
							startIcon={<AddIcon />}
							onClick={handleNovoTipo}
						>
							{i18n.t("minhasSolicitacoes.catalogo.newButton")}
						</Button>
					)}
				</div>
			)}

			{mainView === MAIN_VIEW_VISAO_GERAL && isAdmin ? (
				<VisaoGeralPanel />
			) : mainView === MAIN_VIEW_CATALOGO && isAdmin ? (
				<CatalogoTiposPanel ref={catalogoRef} />
			) : mainView === MAIN_VIEW_RELATORIOS && isAdmin ? (
				<RelatoriosPanel />
			) : (
				<>
			{/* Todos os filtros da tela num unico lugar organizado: Exibir e
			    Buscar (que antes ficavam soltos no cabecalho) ao lado de
			    Tipo/Atendente/SLA (decisao aprovada) - sempre visivel, nao mais
			    um painel escondido atras de um botao. */}
			<Paper className={classes.filtersPaper} variant="outlined">
				<Grid container spacing={1} alignItems="center">
					{isAdmin && (
						<Grid item xs={6} sm={4} md={2}>
							<TextField
								id="minhas-solicitacoes-escopo"
								select
								fullWidth
								size="small"
								variant="outlined"
								label={i18n.t("minhasSolicitacoes.escopo.label")}
								value={scope}
								onChange={e => setScope(e.target.value)}
								SelectProps={{ native: true }}
							>
								<option value={SCOPE_TODAS}>
									{i18n.t("minhasSolicitacoes.escopo.todas")}
								</option>
								<option value={SCOPE_MINHAS}>
									{i18n.t("minhasSolicitacoes.escopo.minhas")}
								</option>
							</TextField>
						</Grid>
					)}
					<Grid item xs={12} sm={8} md={4}>
						<TextField
							id="minhas-solicitacoes-search"
							fullWidth
							size="small"
							variant="outlined"
							label={i18n.t("minhasSolicitacoes.filtros.busca.label")}
							placeholder={i18n.t("minhasSolicitacoes.searchPlaceholder")}
							type="search"
							value={searchParam}
							onChange={handleSearch}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<SearchIcon color="secondary" fontSize="small" />
									</InputAdornment>
								),
							}}
						/>
					</Grid>
					<Grid item xs={6} sm={4} md={2}>
						<TextField
							id="minhas-solicitacoes-filtro-tipo"
							select
							fullWidth
							size="small"
							variant="outlined"
							label={i18n.t("minhasSolicitacoes.filtros.tipo.label")}
							value={filters.typeId}
							onChange={handleFilterChange("typeId")}
						>
							<MenuItem value="">
								{i18n.t("minhasSolicitacoes.filtros.tipo.todos")}
							</MenuItem>
							{requestTypes.map(t => (
								<MenuItem key={t.id} value={t.id}>
									{t.name}
								</MenuItem>
							))}
						</TextField>
					</Grid>
					{isAdmin && (
						<Grid item xs={6} sm={4} md={2}>
							<Tooltip
								title={
									scope === SCOPE_MINHAS
										? i18n.t("minhasSolicitacoes.filtros.atendente.disabledHint")
										: ""
								}
							>
								<TextField
									id="minhas-solicitacoes-filtro-atendente"
									select
									fullWidth
									size="small"
									variant="outlined"
									label={i18n.t("minhasSolicitacoes.filtros.atendente.label")}
									value={filters.attendantId}
									onChange={handleFilterChange("attendantId")}
									disabled={scope === SCOPE_MINHAS}
								>
									<MenuItem value="">
										{i18n.t("minhasSolicitacoes.filtros.atendente.todos")}
									</MenuItem>
									{users.map(u => (
										<MenuItem key={u.id} value={String(u.id)}>
											{u.name}
										</MenuItem>
									))}
								</TextField>
							</Tooltip>
						</Grid>
					)}
					<Grid item xs={6} sm={4} md={2}>
						<TextField
							id="minhas-solicitacoes-filtro-sla"
							select
							fullWidth
							size="small"
							variant="outlined"
							label={i18n.t("minhasSolicitacoes.filtros.sla.label")}
							value={filters.slaSituacao}
							onChange={handleFilterChange("slaSituacao")}
						>
							<MenuItem value="">
								{i18n.t("minhasSolicitacoes.filtros.sla.todos")}
							</MenuItem>
							{SLA_SITUACOES.map(s => (
								<MenuItem key={s} value={s}>
									{i18n.t(`minhasSolicitacoes.filtros.sla.${s}`)}
								</MenuItem>
							))}
						</TextField>
					</Grid>
				</Grid>
				<div className={classes.filtersActions}>
					<Button
						id="minhas-solicitacoes-filtros-limpar-btn"
						size="small"
						onClick={handleClearFilters}
						disabled={!hasActiveFilters}
					>
						{i18n.t("minhasSolicitacoes.filtros.limpar")}
					</Button>
				</div>
			</Paper>

			<div className={classes.tabsRow}>
				<Tabs
					value={statusFilter === CANCELLED_KEY ? false : statusFilter}
					onChange={handleChangeTab}
					indicatorColor="primary"
					textColor="primary"
					variant="scrollable"
					scrollButtons="auto"
					className={classes.tabsGrow}
				>
					{STATUS_TABS.map(tab => (
						<Tab
							key={tab.key}
							id={`minhas-solicitacoes-tab-${tab.key}`}
							value={tab.key}
							classes={{ root: classes.tabRoot }}
							label={
								<span className={classes.tabLabel}>
									{i18n.t(`minhasSolicitacoes.tabs.${tab.key}`)} ({tabCounts[tab.key] ?? "…"})
								</span>
							}
						/>
					))}
				</Tabs>
				<Tooltip title={i18n.t("minhasSolicitacoes.tabs.cancelledTooltip")}>
					<Chip
						id="minhas-solicitacoes-tab-cancelled"
						size="small"
						variant={statusFilter === CANCELLED_KEY ? "default" : "outlined"}
						color={statusFilter === CANCELLED_KEY ? "primary" : "default"}
						className={classes.cancelledChip}
						label={`${i18n.t("minhasSolicitacoes.tabs.cancelled")} (${cancelledCount})`}
						onClick={handleShowCancelled}
					/>
				</Tooltip>
			</div>

			<Paper
				id="minhas-solicitacoes-table"
				className={classes.mainPaper}
				variant="outlined"
				onScroll={handleScroll}
			>
				{fetchError && solicitacoes.length === 0 && !loading ? (
					<div id="minhas-solicitacoes-error" className={classes.errorState}>
						<ErrorOutlineIcon fontSize="large" />
						<Typography variant="body1" style={{ marginTop: 8 }}>
							{i18n.t("minhasSolicitacoes.errorState")}
						</Typography>
						<Button
							variant="outlined"
							color="primary"
							onClick={handleRefresh}
							style={{ marginTop: 12 }}
						>
							{i18n.t("minhasSolicitacoes.retry")}
						</Button>
					</div>
				) : (
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell align="center">
									{i18n.t("minhasSolicitacoes.columns.number")}
								</TableCell>
								<TableCell align="left">
									{i18n.t("minhasSolicitacoes.columns.client")}
								</TableCell>
								<TableCell align="center">
									{i18n.t("minhasSolicitacoes.columns.type")}
								</TableCell>
								<TableCell align="center">
									{i18n.t("minhasSolicitacoes.columns.status")}
								</TableCell>
								<TableCell align="center">
									{i18n.t("minhasSolicitacoes.columns.sla")}
								</TableCell>
								<TableCell align="center" className={classes.hideOnMobile}>
									{i18n.t("minhasSolicitacoes.columns.attendant")}
								</TableCell>
								<TableCell align="center" className={classes.hideOnMobile}>
									{i18n.t("minhasSolicitacoes.columns.createdAt")}
								</TableCell>
								<TableCell align="center" className={classes.hideOnMobile}>
									{i18n.t("minhasSolicitacoes.columns.updatedAt")}
								</TableCell>
								<TableCell align="center">
									{i18n.t("minhasSolicitacoes.columns.actions")}
								</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{!loading && solicitacoes.length === 0 && !fetchError && (
								<TableRow>
									<TableCell colSpan={9}>
										<div id="minhas-solicitacoes-empty" className={classes.emptyState}>
											<Typography variant="body1">
												{i18n.t("minhasSolicitacoes.emptyState")}
											</Typography>
										</div>
									</TableCell>
								</TableRow>
							)}
							{solicitacoes.map(item => {
								const color = statusColor(item.status);
								return (
									<TableRow
										key={item.id}
										id={`minhas-solicitacoes-item-${item.number}`}
										hover
										style={{ cursor: "pointer" }}
										onClick={() => handleRowClick(item.id)}
									>
										<TableCell align="center">#{item.number}</TableCell>
										<TableCell
											align="left"
											className={classes.clienteCell}
											title={nomeDoCliente(item)}
										>
											{/* Sem uuid nao ha para onde navegar: renderiza texto puro
											    em vez de um link que levaria a /tickets/undefined. */}
											{item.ticket_whaticket_uuid ? (
												<Link
													component={RouterLink}
													to={`/tickets/${item.ticket_whaticket_uuid}`}
													onClick={e => e.stopPropagation()}
												>
													{nomeDoCliente(item)}
												</Link>
											) : (
												nomeDoCliente(item)
											)}
										</TableCell>
										<TableCell align="center">
											{item.type?.name || "-"}
										</TableCell>
										<TableCell align="center">
											<Chip
												size="small"
												className={classes.statusChip}
												label={statusLabel(item.status)}
												style={{
													backgroundColor: color.background,
													color: color.color,
												}}
											/>
										</TableCell>
										<TableCell align="center" onClick={e => e.stopPropagation()}>
											<SlaProgress sla={item.sla} />
										</TableCell>
										<TableCell align="center" className={classes.hideOnMobile}>
											{item.attendant_name || "-"}
										</TableCell>
										<TableCell align="center" className={classes.hideOnMobile}>
											{format(parseISO(item.created_at), "dd/MM/yyyy HH:mm")}
										</TableCell>
										<TableCell align="center" className={classes.hideOnMobile}>
											{format(parseISO(item.updated_at), "dd/MM/yyyy HH:mm")}
										</TableCell>
										<TableCell align="center">
											<Tooltip title={i18n.t("minhasSolicitacoes.viewDetails")}>
												<IconButton
													size="small"
													onClick={e => {
														e.stopPropagation();
														handleRowClick(item.id);
													}}
												>
													<VisibilityIcon fontSize="small" />
												</IconButton>
											</Tooltip>
										</TableCell>
									</TableRow>
								);
							})}
							{loading && <TableRowSkeleton columns={9} />}
						</TableBody>
					</Table>
				)}
			</Paper>
				</>
			)}
		</MainContainer>
	);
};

export default MinhasSolicitacoes;
